import { defineComponent, toRefs, ref, computed, watch, nextTick, createVNode, mergeProps } from "vue";
import { getPrefixCls } from "../_utils/global-config.js";
import { isArray, isObject, isFunction } from "../_utils/is.js";
import { getValueDataFromModelValue } from "./utils.js";
import Trigger from "../trigger/index.js";
import SelectView from "../_components/select-view/select-view.js";
import { getKeyDownHandler, CODE } from "../_utils/keyboard.js";
import usePickSlots from "../_hooks/use-pick-slots.js";
import Dropdown from "../_components/dropdown/dropdown.js";
import DropDownOption from "../_components/dropdown/dropdown-option.js";
import DropDownOptGroup from "../_components/dropdown/dropdown-optgroup.js";
import { useOptions } from "../_hooks/use-options.js";
import VirtualList from "../_components/virtual-list/virtual-list.js";
var _Select = defineComponent({
  name: "Select",
  inheritAttrs: false,
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: [String, Number, Array]
    },
    defaultValue: {
      type: [String, Number, Array],
      default: (props) => props.multiple ? [] : ""
    },
    inputValue: {
      type: String
    },
    defaultInputValue: {
      type: String,
      default: ""
    },
    size: {
      type: String,
      default: "medium"
    },
    placeholder: String,
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    allowClear: {
      type: Boolean,
      default: false
    },
    allowSearch: {
      type: Boolean,
      default: (props) => props.multiple
    },
    allowCreate: {
      type: Boolean,
      default: false
    },
    maxTagCount: {
      type: Number,
      default: 0
    },
    popupContainer: {
      type: [String, Object]
    },
    defaultActiveFirstOption: {
      type: Boolean,
      default: true
    },
    popupVisible: {
      type: Boolean,
      default: void 0
    },
    unmountOnClose: {
      type: Boolean,
      default: true
    },
    filterOption: {
      type: Boolean,
      default: true
    },
    options: {
      type: Array,
      default: () => []
    },
    virtualListProps: {
      type: Object
    },
    triggerProps: {
      type: Object
    },
    formatLabel: {
      type: Function
    },
    onChange: {
      type: [Function, Array]
    },
    onInputValueChange: {
      type: [Function, Array]
    },
    onPopupVisibleChange: {
      type: [Function, Array]
    },
    onClear: {
      type: [Function, Array]
    },
    onRemove: {
      type: [Function, Array]
    },
    onSearch: {
      type: [Function, Array]
    }
  },
  emits: [
    "update:modelValue",
    "change",
    "inputValueChange",
    "popupVisibleChange",
    "clear",
    "remove",
    "search",
    "dropdownScroll",
    "dropdownReachBottom"
  ],
  setup(props, {
    slots,
    emit,
    attrs
  }) {
    const prefixCls = getPrefixCls("select");
    const {
      options,
      filterOption
    } = toRefs(props);
    const defaultSlot = usePickSlots(slots, "default");
    const dropdownRef = ref();
    const optionRefs = ref({});
    const _value = ref(props.defaultValue);
    const _inputValue = ref("");
    const _popupVisible = ref(false);
    const computedValue = computed(() => {
      var _a, _b;
      const mergedValue = (_a = props.modelValue) != null ? _a : _value.value;
      if (props.multiple) {
        if (!isArray(mergedValue)) {
          return mergedValue ? [mergedValue] : [];
        }
      } else if (isArray(mergedValue)) {
        return (_b = mergedValue[0]) != null ? _b : "";
      }
      return mergedValue;
    });
    const computedInputValue = computed(() => {
      var _a;
      return (_a = props.inputValue) != null ? _a : _inputValue.value;
    });
    const computedPopupVisible = computed(() => {
      var _a;
      return (_a = props.popupVisible) != null ? _a : _popupVisible.value;
    });
    const retainInputValue = computed(() => isObject(props.allowSearch) && Boolean(props.allowSearch.retainInputValue));
    const createdOptions = computed(() => {
      const options2 = [];
      if (props.allowCreate) {
        if (isArray(computedValue.value)) {
          options2.push(...computedValue.value);
        } else if (computedValue.value) {
          options2.push(computedValue.value);
        }
        if (computedInputValue.value && !options2.includes(computedInputValue.value)) {
          options2.push(computedInputValue.value);
        }
      }
      return options2;
    });
    const virtualListRef = ref();
    const {
      optionNodes,
      optionInfoMap,
      enabledOptionSet,
      activeOption,
      getNextActiveOption,
      scrollIntoView
    } = useOptions({
      defaultSlot,
      options,
      extraOptions: createdOptions,
      inputValue: computedInputValue,
      filterOption,
      dropdownRef,
      optionRefs,
      virtualListRef
    });
    const updateValue = (value) => {
      _value.value = value;
      emit("update:modelValue", value);
      emit("change", value);
    };
    const handlePopupVisibleChange = (visible) => {
      if (computedPopupVisible.value !== visible) {
        _popupVisible.value = visible;
        emit("popupVisibleChange", visible);
      }
    };
    const checkOption = (value) => {
      if (isArray(computedValue.value) && enabledOptionSet.has(value)) {
        const newValue = computedValue.value.concat(value);
        updateValue(newValue);
      }
    };
    const uncheckOption = (value) => {
      if (isArray(computedValue.value)) {
        const newValue = computedValue.value.filter((v) => v !== value);
        updateValue(newValue);
      }
    };
    const handleSelect = (value, e) => {
      if (props.multiple) {
        if (isArray(computedValue.value)) {
          if (computedValue.value.includes(value)) {
            uncheckOption(value);
          } else {
            checkOption(value);
          }
        }
        if (!retainInputValue.value) {
          handleInputValueChange("", "optionChecked");
        }
      } else {
        if (value !== computedValue.value) {
          updateValue(value);
        }
        handlePopupVisibleChange(false);
      }
    };
    const handleMouseEnter = (value, e) => {
      const optionInfo = optionInfoMap.get(value);
      if (optionInfo) {
        activeOption.value = optionInfo;
      }
    };
    const handleMouseLeave = (e) => {
      activeOption.value = void 0;
    };
    const handleDropdownScroll = (e) => {
      emit("dropdownScroll", e);
    };
    const handleDropdownReachBottom = (e) => {
      emit("dropdownReachBottom", e);
    };
    const handleInputValueChange = (value, reason) => {
      if (value !== computedInputValue.value) {
        if (reason === "manual" && !computedPopupVisible.value) {
          _popupVisible.value = true;
          emit("popupVisibleChange", true);
        }
        _inputValue.value = value;
        emit("inputValueChange", value);
        if (props.allowSearch) {
          emit("search", value);
        }
      }
    };
    const handleKeyDown = getKeyDownHandler(new Map([
      [CODE.ENTER, (e) => {
        if (computedPopupVisible.value) {
          if (activeOption.value) {
            handleSelect(activeOption.value.value);
          }
        } else {
          handlePopupVisibleChange(true);
        }
        e.preventDefault();
      }],
      [CODE.ESC, (e) => {
        handlePopupVisibleChange(false);
        e.preventDefault();
      }],
      [CODE.ARROW_DOWN, (e) => {
        const next = getNextActiveOption("down");
        if (next) {
          activeOption.value = next;
          scrollIntoView(next.value);
        }
        e.preventDefault();
      }],
      [CODE.ARROW_UP, (e) => {
        const next = getNextActiveOption("up");
        if (next) {
          activeOption.value = next;
          scrollIntoView(next.value);
        }
        e.preventDefault();
      }]
    ]));
    const handleRemove = (tag) => {
      if (isArray(computedValue.value)) {
        const newValue = computedValue.value.filter((v) => v !== tag);
        updateValue(newValue);
        emit("remove", tag);
      }
    };
    const handleClear = (e) => {
      e == null ? void 0 : e.stopPropagation();
      if (isArray(computedValue.value)) {
        const newValue = computedValue.value.filter((v) => {
          const optionInfo = optionInfoMap.get(v);
          return Boolean(optionInfo == null ? void 0 : optionInfo.disabled);
        });
        updateValue(newValue);
      } else {
        updateValue("");
      }
      handleInputValueChange("", "manual");
      emit("clear");
    };
    watch(computedPopupVisible, (visible) => {
      if (visible) {
        const currentValue = isArray(computedValue.value) ? computedValue.value[0] : computedValue.value;
        activeOption.value = enabledOptionSet.has(currentValue) ? optionInfoMap.get(currentValue) : optionInfoMap.get(Array.from(enabledOptionSet)[0]);
        nextTick(() => {
          var _a;
          if ((_a = activeOption.value) == null ? void 0 : _a.value) {
            scrollIntoView(activeOption.value.value);
          }
        });
      } else if (!retainInputValue.value) {
        handleInputValueChange("", "optionListHide");
      }
    });
    const getOptionContentFunc = (item) => {
      if (isFunction(slots.option) && item.value) {
        const optionInfo = optionInfoMap.get(item.value);
        const optionSlot = slots.option;
        return () => optionSlot({
          data: optionInfo
        });
      }
      if (isFunction(item.render)) {
        return item.render;
      }
      return () => item.label;
    };
    const renderOption = (item) => {
      if (item.type === "optGroup") {
        return createVNode(DropDownOptGroup, {
          key: item.key,
          label: item.label
        });
      }
      const {
        value = ""
      } = item;
      const isSelected = isArray(computedValue.value) ? computedValue.value.includes(value) : value === computedValue.value;
      return createVNode(DropDownOption, {
        ref: (ref2) => {
          if (ref2 == null ? void 0 : ref2.$el) {
            optionRefs.value[value] = ref2.$el;
          }
        },
        key: item.key,
        value: item.value,
        disabled: item.disabled,
        isSelected,
        isActive: activeOption.value && value === activeOption.value.value,
        multiple: props.multiple,
        onClick: handleSelect,
        onMouseenter: handleMouseEnter,
        onMouseleave: handleMouseLeave
      }, {
        default: getOptionContentFunc(item)
      });
    };
    const renderDropDown = () => {
      return createVNode(Dropdown, {
        "ref": dropdownRef,
        "class": `${prefixCls}-dropdown`,
        "loading": props.loading,
        "isEmpty": optionNodes.value.length === 0,
        "onScroll": handleDropdownScroll,
        "onReachBottom": handleDropdownReachBottom,
        "virtualList": Boolean(props.virtualListProps)
      }, {
        default: () => [props.virtualListProps ? createVNode(VirtualList, mergeProps({
          "ref": virtualListRef
        }, props.virtualListProps, {
          "data": optionNodes.value
        }), {
          item: ({
            item
          }) => renderOption(item)
        }) : optionNodes.value.map((item) => renderOption(item))],
        empty: slots.empty,
        footer: slots.footer
      });
    };
    return () => createVNode(Trigger, mergeProps({
      "trigger": "click",
      "disabled": props.disabled,
      "position": "bl",
      "popupOffset": 4,
      "animationName": "slide-dynamic-origin",
      "popupVisible": computedPopupVisible.value,
      "unmountOnClose": props.unmountOnClose,
      "hideEmpty": true,
      "preventFocus": true,
      "popupContainer": props.popupContainer,
      "onPopupVisibleChange": handlePopupVisibleChange,
      "autoFitPopupWidth": true,
      "autoFitTransformOrigin": true
    }, props.triggerProps), {
      default: () => [createVNode(SelectView, mergeProps({
        "modelValue": getValueDataFromModelValue(computedValue.value, optionInfoMap),
        "inputValue": computedInputValue.value,
        "multiple": props.multiple,
        "disabled": props.disabled,
        "error": props.error,
        "allowClear": props.allowClear,
        "allowCreate": props.allowCreate,
        "allowSearch": props.allowSearch,
        "opened": computedPopupVisible.value,
        "maxTagCount": props.maxTagCount,
        "placeholder": props.placeholder,
        "size": props.size,
        "formatLabel": props.formatLabel,
        "onInputValueChange": handleInputValueChange,
        "onRemove": handleRemove,
        "onClear": handleClear,
        "onKeydown": handleKeyDown
      }, attrs), {
        label: slots.label
      })],
      content: renderDropDown
    });
  }
});
export { _Select as default };
